import KestrelFilled from 'assets/kestrel-filled.svg?react'
import {
  AggregateUnitData,
  HealthScorePill,
  useSiteViewColors,
} from 'features/overview'
import { MapInteractionAPIs } from 'features/siteMap/SiteViewMap'
import isNil from 'lodash/isNil'
import React from 'react'
import styled from 'styled-components'
import { ForecastTheme } from 'styles/themes'
import { AddPropsToFn, GeckoRiskAssessmentConfig } from 'types'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 6px;
  gap: 6px;
`

const ErpIdDisplay = styled.small<{ $color: string }>`
  color: ${({ $color }) => $color};
`

const HealthScoreContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`

const HealthScoreText = styled.small<{ $color: string }>`
  display: flex;
  flex: 1;
  color: ${({ $color }) => $color};
  font-size: smaller;
`

const EnhancedModelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const EnhancedModelIcon = styled(KestrelFilled)<{ $color: string }>`
  width: 1rem;
  height: 1rem;
  color: ${({ $color }) => $color};
  fill: ${({ $color }) => $color};
`

const EnhancedModelText = styled.small<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-size: smaller;
`

export const getExtraLabelDisplay: AddPropsToFn<
  NonNullable<MapInteractionAPIs['getExtraLabelDisplay']>,
  {
    indexableAggregateData: undefined | Record<string, AggregateUnitData>
    // we need to take in colors as a prop because React-three-fiber seems to be
    // calling our components outside our contexts.
    // TODO [ARCH-330]: Make labels work with contexts
    siteViewColors: ReturnType<typeof useSiteViewColors>
    theme: ForecastTheme
    selectedYear: number
    riskAssessmentConfig: GeckoRiskAssessmentConfig | undefined
  }
> = (
  unitID,
  {
    indexableAggregateData,
    siteViewColors,
    theme,
    selectedYear,
    riskAssessmentConfig,
  }
) => {
  if (isNil(indexableAggregateData) || isNil(unitID)) {
    return null
  }

  const aggDataVal = indexableAggregateData[unitID]

  if (!aggDataVal) {
    return null
  }

  const erpID = aggDataVal.unitSpecs.unitData.data.erp_id
  const erpIDDisplay = isNil(erpID) ? null : (
    <ErpIdDisplay $color={theme.defaultText}>{erpID}</ErpIdDisplay>
  )

  const healthScoreDisplay = (
    <HealthScoreContainer>
      <HealthScoreText $color={theme.defaultText}>Health: </HealthScoreText>
      <HealthScorePill
        healthScoreData={{
          minRL: aggDataVal.health[selectedYear]?.minRLFromYear,
          percentBelowTMin: aggDataVal.health[selectedYear]?.percentBelowTMin,
          healthScore: aggDataVal.health[selectedYear].unitHealthScoreForYear,
          riskScore: aggDataVal.health[selectedYear]?.riskScore,
        }}
        siteViewColors={siteViewColors}
        textContainer={<HealthScoreText $color={theme.defaultText} />}
        riskAssessmentConfig={riskAssessmentConfig}
      />
    </HealthScoreContainer>
  )

  const enhancedModelDisplay = aggDataVal.unitSpecs.kestrelRolodexEntry ? (
    <EnhancedModelContainer>
      <EnhancedModelIcon $color={theme.defaultText} />
      <EnhancedModelText $color={theme.defaultText}>
        Model Available
      </EnhancedModelText>
    </EnhancedModelContainer>
  ) : null

  return (
    <Container>
      {erpIDDisplay}
      {healthScoreDisplay}
      {enhancedModelDisplay}
    </Container>
  )
}
